import type { SimulationSummaryTO } from '@control-center/control-center-client-public-domain-simulation-ts';
import NoContent from 'features/settings/NoContent';
import TableError from 'features/settings/TableError';
import TableSkeleton from 'features/settings/TableSkeleton';

import SimulationRow from './SimulationRow';

export const tableHeaders = {
    vin: 'VIN',
    scenario: 'Scenario',
    state: 'State',
    online: 'Online',
    missionId: 'Mission ID',
    startedAt: 'Started At',
    createdAt: 'Created At',
    actions: 'Actions',
};

interface Props {
    simulations: SimulationSummaryTO[] | undefined;
    scenarioNames: Map<string, string>;
    isLoading: boolean;
    isError: boolean;
    selectedSimulationVin: string | null;
    refresh(): void;
    onSelectVin(vin: string): void;
    onStart(vin: string): void;
    onDelete(vin: string): void;
    onToggleConnectivity(vin: string): void;
    onTriggerMrm(vin: string): void;
}

const SimulationsTable = ({
    simulations,
    scenarioNames,
    isLoading,
    isError,
    selectedSimulationVin,
    refresh,
    onSelectVin,
    onStart,
    onDelete,
    onToggleConnectivity,
    onTriggerMrm,
}: Props) => {
    if (isLoading) {
        return <TableSkeleton id='domain-simulation' tableHeaders={tableHeaders} />;
    }

    if (isError) {
        return (
            <div className='TableContentWrapper'>
                <TableError
                    id='domain-simulation'
                    header='An error occurred while fetching simulations'
                    onClick={refresh}
                />
            </div>
        );
    }

    if (!simulations?.length) {
        return (
            <NoContent
                id='domain-simulation'
                title='No simulations yet'
                body='There are currently no active simulations.'
            />
        );
    }

    return (
        <div className='display-flex flex-column' data-testid='domain-sim-table-wrapper'>
            <table data-testid='domain-sim-table' className='table-head-filled table-hover table-bordered table'>
                <thead>
                    <tr>
                        {Object.values(tableHeaders).map(label => (
                            <th key={label} data-testid='domain-sim-table-header'>
                                {label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {simulations.map(simulation => (
                        <SimulationRow
                            key={simulation.vin}
                            simulation={simulation}
                            scenarioNames={scenarioNames}
                            isSelected={simulation.vin === selectedSimulationVin}
                            onSelect={onSelectVin}
                            onStart={onStart}
                            onDelete={onDelete}
                            onToggleConnectivity={onToggleConnectivity}
                            onTriggerMrm={onTriggerMrm}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default SimulationsTable;
